import { Dialog, Transition } from '@headlessui/react';
import { XIcon } from '@heroicons/react/outline';
import { FilterIcon } from '@heroicons/react/solid';
import useTranslation from 'next-translate/useTranslation';
import { Fragment, SetStateAction, useState, Dispatch } from 'react';
import { MobileProductsFilter } from '../Form/MobileProductsFilter';
import ProductList from './ProductList';
import { ProductsFilter } from '../Form/ProductsFilter/ProductsFilter';
import { SortFilter } from '@/components/Form/ProductsFilter/SortFilter';
import { SearchBar } from '@/components/Form/ProductsFilter/SearchBar';
import useProducts from '@/lib/hooks/fetchers/useProducts';

/* eslint-disable no-unused-vars */
function MobileFilter(props: {
  mobileFiltersOpen: boolean;
  setMobileFiltersOpen: (_x: boolean) => void;
}) {
  const { t } = useTranslation();
  return (
    <Transition.Root show={props.mobileFiltersOpen} as={Fragment}>
      <Dialog
        as='div'
        className='fixed inset-0 z-[51] flex lg:hidden'
        onClose={props.setMobileFiltersOpen}
      >
        <Transition.Child
          as={Fragment}
          enter='transition-opacity ease-linear duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='transition-opacity ease-linear duration-300'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <Dialog.Overlay className='fixed inset-0 bg-black bg-opacity-25' />
        </Transition.Child>

        <Transition.Child
          as={Fragment}
          enter='transition ease-in-out duration-300 transform'
          enterFrom='translate-x-full'
          enterTo='translate-x-0'
          leave='transition ease-in-out duration-300 transform'
          leaveFrom='translate-x-0'
          leaveTo='translate-x-full'
        >
          <div className='relative ml-auto flex h-full w-full max-w-xs flex-col overflow-y-auto bg-white py-4 shadow-xl'>
            <div className='flex items-center justify-between px-6'>
              <h2 className='text-lg font-medium text-gray-900'>
                {t('common:Filters')}
              </h2>
              <button
                type='button'
                className='-mr-2 flex h-10 w-10 items-center justify-center rounded-md bg-white p-2 text-gray-400'
                onClick={() => props.setMobileFiltersOpen(false)}
              >
                <span className='sr-only'>Close menu</span>
                <XIcon className='h-6 w-6' aria-hidden='true' />
              </button>
            </div>
            <MobileProductsFilter />
          </div>
        </Transition.Child>
      </Dialog>
    </Transition.Root>
  );
}

function ProductsViewHeader(props: {
  setMobileFiltersOpen: Dispatch<SetStateAction<boolean>>;
}) {
  return (
    <div className='md:flex-rowitems-baseline relative z-10 mx-auto flex max-w-2xl flex-col justify-between pt-4 sm:px-6 md:pt-6 lg:mx-0 lg:mr-0 lg:max-w-none lg:px-8 lg:pt-8'>
      <SR />
      <div className='flex flex-wrap items-center justify-between'>
        <SearchBar />
        <div className='flex items-center'>
          <SortFilter />
          <button
            type='button'
            className='-m-2 ml-4 p-2 text-gray-400 hover:text-gray-500 sm:ml-6 lg:hidden'
            onClick={() => props.setMobileFiltersOpen(true)}
          >
            <span className='sr-only'>Filters</span>
            <FilterIcon className='h-5 w-5' aria-hidden='true' />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ProductsView() {
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const { productsResponse, isLoading } = useProducts();

  return (
    <div className='bg-white'>
      <MobileFilter
        mobileFiltersOpen={mobileFiltersOpen}
        setMobileFiltersOpen={setMobileFiltersOpen}
      />
      <main className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
        <section aria-labelledby='products-heading' className='pb-24'>
          <div className='grid grid-cols-1 gap-x-8 gap-y-10 lg:grid-cols-4'>
            <ProductsFilter />

            <div className='lg:col-span-3'>
              <ProductsViewHeader setMobileFiltersOpen={setMobileFiltersOpen} />
              <ProductList
                products={productsResponse?.results ?? []}
                isLoading={isLoading}
              />
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
// For Screen Readers (Acccessiblity)
function SR() {
  const { t } = useTranslation();
  return (
    <div className='sr-only flex flex-col justify-around space-y-2'>
      <h2 className='title text-center'>
        {t('common:Buy Used Beverage Machines')}
      </h2>
      <h3 className='subtitle hidden '>
        {t('common:Check out our current used beverage machinery offers')}
      </h3>
    </div>
  );
}
